import { Box, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getBusinessType,
  getRebookCancelPolicy,
  getMembers,
  setBusinessType,
  setMemberData,
} from "@/features/quote/quoteSlice";
import { useRouter } from "next/router";
import { MUIButton } from "@/components/core/material-ui";
import {
  ADULT_MEMBERS,
  BUSINESS_TYPE,
  CLICK_AWAY_EVENT,
  QUOTE_MESSAGES,
  PAGE_ROUTES,
  RELATIONSHIP_MASTER_MEMBERS,
  SEVERITY,
  CANCEL_REBOOK_OPTIONS,
} from "@/config/constants";
import MemberSelection from "@/components/member-selection";
import MuiSwitchOld from "@/components/core/material-ui/SwitchOld";
import AigSnackbar from "@/components/core/aig-snackbar";
import logger from "@/helpers/logger";
import { addGaEvent, initializeGA, pageView } from "@/helpers/analytics";
import { isEmpty } from "lodash";
import ReCAPTCHA from "react-google-recaptcha";
import { CAPTCHA_HMAC_SECRET_KEY, CAPTCHA_SITE_KEY } from "@/config/config";
import * as crypto from "crypto";
import { showLoader } from "@/features/loader/loaderSlice";
import { extractValues } from "@/helpers/utils";
import { getProposal } from "@/features/proposal/proposalSlice";

const MembersComponent = () => {
  //redux data
  const dispatch = useDispatch();
  const router = useRouter();
  const resultArray = [];
  const allMembers = useSelector(getMembers);
  const relationshipCounts = {};
  const rebookCancelPolicy = useSelector(getRebookCancelPolicy);
  const recaptchaRef = useRef(null);
  const proposal = useSelector(getProposal);
  const businessType = proposal?.businessType || "";

  for (const item of allMembers) {
    const { relation } = item;
    if (relation) {
      // Extract the relationship name (e.g., "Son", "Daughter") from the string
      const relationshipName = relation.replace(/\d+/g, "").trim();
      if (!relationshipCounts[relationshipName]) {
        relationshipCounts[relationshipName] = 1;
      } else {
        relationshipCounts[relationshipName]++;
      }
    }
  }

  for (const relationshipName in relationshipCounts) {
    const count = relationshipCounts[relationshipName];
    if (
      relationshipName === RELATIONSHIP_MASTER_MEMBERS["Son"] ||
      relationshipName === RELATIONSHIP_MASTER_MEMBERS["Daughter"]
    ) {
      resultArray.push({ relation: relationshipName, count, multiple: true });
    } else {
      resultArray.push({ relation: relationshipName });
    }
  }
  const reduxArray = resultArray?.map((relation) => ({
    ...relation,
    isSelected: true,
  }));

  const initialData = Object.keys(RELATIONSHIP_MASTER_MEMBERS).map(
    (key, index) => {
      if (
        key === RELATIONSHIP_MASTER_MEMBERS.Son ||
        key === RELATIONSHIP_MASTER_MEMBERS.Daughter
      ) {
        return {
          relation: key,
          id: index,
          count: 0,
          isSelected: false,
          multiple: true,
        };
      } else
        return {
          relation: key,
          id: index,
          isSelected: false,
          multiple: false,
        };
    }
  );

  const selected = useSelector(getBusinessType);
  const [message, setMessage] = useState("");
  const [showSnackBar, setShowSnackBar] = useState(false);
  const [combineData, setCombineData] = useState(
    reduxArray?.length !== 0
      ? initialData.map((data, index) => {
          const member = reduxArray?.find(
            (member) => member.relation === data.relation
          );

          return member ? { ...member, id: index } : data;
        })
      : initialData
  );

  logger.log("test version::: 3.4 New");
  useEffect(() => {
    if (typeof window.grecaptcha !== "undefined") {
      logger.log("reCAPTCHA ready!");
    }
  }, []);

  const handleClose = (event, reason) => {
    if (reason === CLICK_AWAY_EVENT) {
      return;
    }
    setShowSnackBar(false);
  };
  const handleSwitchChange = () => {
    if (
      businessType === BUSINESS_TYPE.RENEWAL &&
      !isEmpty(rebookCancelPolicy)
    ) {
      setShowSnackBar(true);
      setMessage(QUOTE_MESSAGES.REBOOK_RENEWAL_TOGGLE);
      return;
    } else {
      addGaEvent("MEMBERS_PORT", `BUSINESS_TYPE_${selected}`, "PORT_SWITCH");
      dispatch(
        setBusinessType(
          selected === BUSINESS_TYPE.NEW
            ? BUSINESS_TYPE.PORT
            : BUSINESS_TYPE.NEW
        )
      );
    }
  };
  const onlyAdultArr = (arr) => {
    return arr.filter((ele) => ADULT_MEMBERS.includes(ele));
  };

  // Check If Member Details is Same as before
  const hasMemberDetailsChanged = (newMemberDetails, oldMemberDetails) => {
    if (newMemberDetails.length > oldMemberDetails.length) {
      return true;
    }

    if (newMemberDetails.length === oldMemberDetails.length) {
      const getNewRelationships = (arr) => arr.map((m) => m.relation).sort();
      const getRelationships = (arr) => arr.map((m) => m.relationship).sort();
      const newRels = getNewRelationships(newMemberDetails);
      const oldRels = getRelationships(oldMemberDetails);

      for (let i = 0; i < newRels.length; i++) {
        if (newRels[i] !== oldRels[i]) {
          return true;
        }
      }
    }
    return false;
  };

  const onSubmit = () => {
    const selectedMembers = combineData.filter((el) => el.isSelected);
    if (selectedMembers.length === 0) {
      setShowSnackBar(true);
      setMessage(QUOTE_MESSAGES.ATLEAST_ONE);
      return;
    }
    if (rebookCancelPolicy === CANCEL_REBOOK_OPTIONS.REBOOK) {
      const arr1 = onlyAdultArr(extractValues(selectedMembers, "relation"));
      const arr2 = onlyAdultArr(extractValues(allMembers, "relation"));
      if (arr1.length > 2) {
        if (arr1.length > arr2.length) {
          setShowSnackBar(true);
          setMessage(QUOTE_MESSAGES.REBOOK_ADULT_MEMBER);
          return;
        } else if (
          arr1.length === arr2.length &&
          arr1.sort().toString() !== arr2.sort().toString()
        ) {
          setShowSnackBar(true);
          setMessage(QUOTE_MESSAGES.REBOOK_ADULT_MEMBER);
          return;
        }
      }
    }

    const resultArray = [];
    for (const item of selectedMembers) {
      if (
        item.relation === RELATIONSHIP_MASTER_MEMBERS.Son ||
        item.relation === RELATIONSHIP_MASTER_MEMBERS.Daughter
      ) {
        // If it's Son or Daughter, and isSelected is true

        const relationArray = [];
        for (let i = 1; i <= item.count; i++) {
          relationArray.push({ relation: `${item.relation} ${i}` });
        }
        resultArray.push(...relationArray);
      } else {
        // For other relationships, just push the object with relation property
        resultArray.push({ relation: item.relation });
      }
    }
    let adultCount = 0;
    let childCount = 0;
    // calculating the count of adultMem and childMem
    for (let member of resultArray) {
      if (ADULT_MEMBERS.includes(member.relation)) {
        adultCount += 1;
      } else {
        childCount += 1;
      }
    }

    // Addition of member not allowed in Renewal and Cancel Rebook Journey
    if (
      businessType === BUSINESS_TYPE.RENEWAL &&
      rebookCancelPolicy === CANCEL_REBOOK_OPTIONS.REBOOK
    ) {
      if (hasMemberDetailsChanged(resultArray, proposal?.memberDetails)) {
        setShowSnackBar(true);
        setMessage(QUOTE_MESSAGES.REBOOK_RENEWAL_ADDITION);
        return;
      }
    }

    if (resultArray.length > 7) {
      setShowSnackBar(true);
      setMessage(QUOTE_MESSAGES.MAX_MEMBERS);
      return;
    } else if (adultCount > 4) {
      setShowSnackBar(true);
      setMessage(QUOTE_MESSAGES.MAX_ADULTS);
      return;
    } else if (childCount > 3) {
      setShowSnackBar(true);
      setMessage(QUOTE_MESSAGES.MAX_CHILDREN);
      return;
    }
    dispatch(setMemberData(resultArray));
    if (recaptchaRef.current) {
      window.grecaptcha.reset();
      recaptchaRef.current.execute();
    } else {
      logger.error("reCAPTCHA reference is null");
    }
  };
  const verifyCaptcha = async (token) => {
    localStorage.setItem("captchaToken", token);
    let deviceId = localStorage.getItem("device_id");
    if (!deviceId) {
      deviceId = crypto.randomBytes(20).toString("hex");
      localStorage.setItem("device_id", deviceId);
    }
    const signature = crypto
      .createHmac("sha256", CAPTCHA_HMAC_SECRET_KEY)
      .update(deviceId)
      .digest("hex");
    addGaEvent("MEMBER_SELECT", "MEMBERS_SELECT", "NEXT");
    recaptchaRef.current.reset();
    dispatch(showLoader(true));
    router.push(
      `${PAGE_ROUTES.MEMBERS_DOB}?token=${token}&deviceId=${deviceId}&signature=${signature}`
    );
  };

  useEffect(() => {
    initializeGA();
    pageView("MEMBERS");
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        mt: { xs: "4rem", md: 0 },
        textAlign: !isEmpty(rebookCancelPolicy) ? "center" : "",
      }}
    >
      <Box
        sx={{
          width: {
            xs: "100%",
            md: !isEmpty(rebookCancelPolicy) ? "55%" : "36%",
            margin: "0 auto",
          },
          px: { xs: "1rem", md: "0rem" },
        }}
      >
        <Box
          sx={{
            marginY: { xs: "1rem", md: "2rem" },
          }}
        >
          <Typography
            sx={{
              typography: {
                xs: "aigHeading3",
                md: "aigHeading1",
              },
              fontWeight: "600",
              color: "color.aigColor7",
            }}
          >
            Select Individuals
          </Typography>
          <Typography
            component={"div"}
            sx={{
              typography: {
                xs: "aigBodyS",
                md: "aigBodyM",
              },
              mt: { xs: "0.5rem", md: "0rem" },
              fontWeight: "fontWeightLight",
              color: "color.aigColor4",
            }}
          >
            Select those who you want to insure
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: {
                xs: "center",
                md: !isEmpty(rebookCancelPolicy) ? "center" : "flex-start",
              },
            }}
          >
            <MuiSwitchOld
              label1="New"
              label2="Port"
              selected={selected}
              handleSwitchChange={handleSwitchChange}
              sx={{
                mt: "0.75rem",
                mx: { xs: "auto", sm: "0rem" },
                maxWidth: "10.75rem",
              }}
            />
          </Box>
        </Box>
        <MemberSelection
          combineData={combineData}
          setCombineData={setCombineData}
          showSnackBar={showSnackBar}
          setShowSnackBar={setShowSnackBar}
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: {
              xs: "center",
              md: !isEmpty(rebookCancelPolicy) ? "center" : "flex-start",
            },
          }}
        >
          <Box
            sx={{
              py: { xs: "2rem", md: "0rem" },
              width: { xs: "100%", md: "11.5rem" },
              zIndex: 40,
              bottom: { xs: "0rem", md: "2rem" },
              background: "white",
            }}
          >
            <MUIButton
              variant="aig-primary"
              type="submit"
              size="medium"
              onClick={() => onSubmit()}
              disabled={combineData.filter((el) => el.isSelected).length === 0}
              sx={{
                borderRadius: "1.5rem",
                width: { xs: "100%", md: "11.5rem" },
                marginTop: { xs: "0rem", md: "3rem" },
                p: "0.75rem 1rem",
                marginY: { xs: "1rem" },
              }}
            >
              Next
            </MUIButton>
            <ReCAPTCHA
              sitekey={CAPTCHA_SITE_KEY}
              size="invisible"
              ref={recaptchaRef}
              onChange={(token) => {
                verifyCaptcha(token);
              }}
            />
          </Box>
        </Box>
      </Box>

      <AigSnackbar
        message={message}
        severity={SEVERITY.WARNING}
        open={showSnackBar}
        handleClose={handleClose}
      />
    </Box>
  );
};

export default MembersComponent;
